import { useState, useRef, useEffect } from 'react';
import { Zap, ChevronDown, RefreshCw, Activity } from 'lucide-react';
import { useVpsStore } from '../../stores/vpsStore';

export default function QuickActionsMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const triggerSync = useVpsStore((s) => s.triggerSync);
  const syncTriggering = useVpsStore((s) => s.syncTriggering);
  const fetchStatus = useVpsStore((s) => s.fetchStatus);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleAction = (action: () => void) => {
    action();
    setOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
        onClick={() => setOpen(!open)}
      >
        <Zap className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Quick Actions</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 rounded-lg border border-border-subtle bg-bg-secondary shadow-xl overflow-hidden z-50">
          <div className="py-1">
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors disabled:opacity-50"
              onClick={() => handleAction(triggerSync)}
              disabled={syncTriggering}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncTriggering ? 'animate-spin' : ''}`} />
              {syncTriggering ? 'Syncing...' : 'Trigger Sync'}
            </button>
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
              onClick={() => handleAction(fetchStatus)}
            >
              <Activity className="w-3.5 h-3.5" />
              Check VPS Health
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
